import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Box, Button, Card, CardContent, CardMedia, Dialog, DialogContent, Grid, IconButton, Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { darken, fade } from '@material-ui/core/styles/colorManipulator';
import CloseIcon from '@material-ui/icons/Close';
import homeScreenshot from '../../assets/screenshots/homepage.png';
import visualTriviaScreenshot from '../../assets/screenshots/visualtrivia.png';
import liveScreenshot from '../../assets/screenshots/live.png';
import settingsScreenshot from '../../assets/screenshots/settings.png';
import Seo from '../Seo/Seo';

const styles = theme => {
    const primary = theme.palette.primary.main;
    const primaryMuted = fade(primary, 0.4);
    const surfaceText = theme.palette.getContrastText('#0b1210');
    const subduedText = fade(surfaceText, 0.78);

    return {
        root: {
            flexGrow: 1,
        },
        hero: {
            minHeight: 'calc(100vh - 70px)',
            background: `radial-gradient(circle at top, ${fade(primary, 0.3)} 0%, ${fade('#0f1413', 0.95)} 48%, #0b0f0e 100%)`,
            color: surfaceText,
            padding: theme.spacing(10, 4, 12),
        },
        heroCard: {
            background: 'linear-gradient(135deg, rgba(17,24,22,0.95), rgba(10,12,12,0.85))',
            border: `1px solid ${fade(primary, 0.35)}`,
            boxShadow: '0 25px 60px rgba(0,0,0,0.45)',
            borderRadius: 24,
            padding: theme.spacing(5),
        },
        kicker: {
            color: primary,
            fontWeight: 600,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            fontSize: 12,
        },
        title: {
            fontSize: 48,
            fontWeight: 800,
            marginTop: theme.spacing(2),
            color: surfaceText,
            '@media (max-width: 768px)': {
                fontSize: 44,
            },
        },
        subtitle: {
            marginTop: theme.spacing(2),
            fontSize: 18,
            lineHeight: 1.6,
            color: subduedText,
        },
        accent: {
            margin: theme.spacing(3, 0),
            height: 4,
            width: 120,
            borderRadius: 999,
            background: `linear-gradient(90deg, ${primary} 0%, ${primaryMuted} 100%)`,
        },
        ctaRow: {
            marginTop: theme.spacing(4),
            display: 'flex',
            flexWrap: 'wrap',
            gap: theme.spacing(2),
        },
        primaryButton: {
            backgroundColor: primary,
            color: theme.palette.getContrastText(primary),
            fontWeight: 700,
            padding: theme.spacing(1.4, 4),
            '&:hover': {
                backgroundColor: darken(primary, 0.08),
            },
        },
        secondaryButton: {
            borderColor: primary,
            color: surfaceText,
            fontWeight: 600,
            padding: theme.spacing(1.4, 4),
        },
        sectionTitle: {
            marginTop: theme.spacing(10),
            marginBottom: theme.spacing(3),
            color: surfaceText,
            fontWeight: 700,
        },
        featureCard: {
            backgroundColor: '#151b1a',
            borderRadius: 20,
            border: '1px solid rgba(255, 255, 255, 0.05)',
            height: '100%',
            color: surfaceText,
            boxShadow: '0 18px 35px rgba(0,0,0,0.35)',
        },
        featureTitle: {
            color: primary,
            fontWeight: 600,
            marginBottom: theme.spacing(1),
        },
        featureBody: {
            color: subduedText,
            lineHeight: 1.6,
        },
        stepsCard: {
            background: 'linear-gradient(120deg, rgba(20,24,22,0.92), rgba(12,15,14,0.92))',
            borderRadius: 20,
            border: '1px solid rgba(255, 255, 255, 0.06)',
            padding: theme.spacing(4),
        },
        storySection: {
            marginTop: theme.spacing(10),
        },
        storyCard: {
            background: 'linear-gradient(140deg, rgba(24,28,28,0.96), rgba(16,18,19,0.94))',
            borderRadius: 24,
            border: '1px solid rgba(255, 255, 255, 0.06)',
            boxShadow: '0 22px 50px rgba(0,0,0,0.35)',
            overflow: 'hidden',
        },
        storyMedia: {
            height: 240,
            width: '100%',
            objectFit: 'cover',
            cursor: 'zoom-in',
            transform: 'scale(1)',
            transition: 'transform 240ms ease, box-shadow 240ms ease',
            '&:focus': {
                outline: 'none',
            },
            '&:focus-visible': {
                boxShadow: `0 0 0 3px ${fade(primary, 0.45)}`,
            },
            '@media (hover: hover)': {
                '&:hover': {
                    transform: 'scale(1.03)',
                    boxShadow: '0 16px 32px rgba(0, 0, 0, 0.35)',
                },
            },
        },
        modalPaper: {
            backgroundColor: '#0c1110',
            width: '92vw',
            maxWidth: 1400,
            height: '90vh',
        },
        modalContent: {
            padding: 0,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        modalClose: {
            position: 'absolute',
            right: theme.spacing(1),
            top: theme.spacing(1),
            color: surfaceText,
        },
        modalImage: {
            width: '100%',
            height: '100%',
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            borderRadius: 12,
        },
        storyContent: {
            padding: theme.spacing(3, 3.5, 3.5),
        },
        storyTitle: {
            color: surfaceText,
            fontWeight: 700,
            marginBottom: theme.spacing(1),
        },
        storyBody: {
            color: subduedText,
            lineHeight: 1.7,
        },
        stepNumber: {
            fontSize: 14,
            color: primary,
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
        },
        stepTitle: {
            color: surfaceText,
            fontWeight: 600,
            marginTop: theme.spacing(1),
        },
        stepBody: {
            color: subduedText,
            marginTop: theme.spacing(1),
            lineHeight: 1.6,
        },
    }
}

class Welcome extends Component {
    state = {
        activeStory: null,
    };

    handleRegisterClick = () => {
        this.props.history.push('/register');
    };

    handleLoginClick = () => {
        if (this.props.user && this.props.user.id) {
            this.props.history.push('/home');
            return;
        }
        this.props.history.push('/login');
    };

    handleStoryOpen = (card) => {
        this.setState({ activeStory: card });
    };

    handleStoryClose = () => {
        this.setState({ activeStory: null });
    };

    handleStoryKeyDown = (event, card) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.handleStoryOpen(card);
        }
    };

    render() {

        const { classes } = this.props;
        const { activeStory } = this.state;
        const storyCards = [
            {
                title: 'Start at Home',
                description: 'Track the current contest, share team updates, and keep everyone aligned in one place.',
                image: homeScreenshot,
            },
            {
                title: 'Solve Visual Trivia Faster',
                description: 'Post image-based prompts and keep teammates focused on the right clues.',
                image: visualTriviaScreenshot,
            },
            {
                title: 'Stay Live & On Time',
                description: 'Follow the live contest view and countdown so your team never misses a question.',
                image: liveScreenshot,
            },
            {
                title: 'Tune the Details',
                description: 'Manage teams, contests, and preferences with a clean, centralized settings panel.',
                image: settingsScreenshot,
            },
        ];

        return (
            <div className={classes.hero}>
                <Seo
                    title="Trivia Weekend | Trivia Marathon Command Center"
                    description="Run marathon trivia nights with a command center built for teams. Coordinate live rounds, visual trivia, answer logging, and team shifts in one place."
                    canonicalPath="/#/"
                    ogType="website"
                    schema={[
                        {
                            '@context': 'https://schema.org',
                            '@type': 'Organization',
                            name: 'Trivia Weekend',
                            url: 'https://triviaweekend.com',
                            logo: 'https://triviaweekend.com/favicon.ico',
                        },
                        {
                            '@context': 'https://schema.org',
                            '@type': 'WebSite',
                            name: 'Trivia Weekend',
                            url: 'https://triviaweekend.com',
                            description: 'Trivia Weekend is a command center for marathon trivia teams.',
                        },
                        {
                            '@context': 'https://schema.org',
                            '@type': 'WebApplication',
                            name: 'Trivia Weekend',
                            applicationCategory: 'GameApplication',
                            operatingSystem: 'Web',
                            url: 'https://triviaweekend.com',
                            description: 'Coordinate teams, track live trivia rounds, and manage visual trivia in a single dashboard.',
                        },
                    ]}
                    schemaId="structured-data-home"
                />
                <Box maxWidth="1100px" margin="0 auto">
                    <Card className={classes.heroCard}>
                        <Typography className={classes.kicker}>Trivia Weekend</Typography>
                        <Typography className={classes.title} component="h1">
                            The command center for marathon trivia crews.
                        </Typography>
                        <div className={classes.accent} />
                        <Typography className={classes.subtitle}>
                            Welcome to a platform built for nonstop trivia. Run your live game, coordinate teams, log answers,
                            and keep every hour organized in one place. Whether you are surviving 50 straight hours or hosting a
                            full-scale trivia marathon, Trivia Weekend keeps the chaos under control.
                        </Typography>
                        <Box className={classes.ctaRow}>
                            <Button
                                className={classes.primaryButton}
                                variant="contained"
                                size="large"
                                onClick={this.handleRegisterClick}
                            >
                                Create a Team
                            </Button>
                            <Button
                                className={classes.secondaryButton}
                                variant="outlined"
                                size="large"
                                onClick={this.handleLoginClick}
                            >
                                Log In
                            </Button>
                        </Box>
                    </Card>

                    <Typography className={classes.sectionTitle} variant="h4">
                        What makes it feel unstoppable
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <Card className={classes.featureCard}>
                                <CardContent>
                                    <Typography className={classes.featureTitle} variant="h6">
                                        Live Game Hub
                                    </Typography>
                                    <Typography className={classes.featureBody}>
                                        Track every hour, question, and score without losing the thread. Stay in rhythm with a
                                        clear view of what is live, what is pending, and what is next.
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Card className={classes.featureCard}>
                                <CardContent>
                                    <Typography className={classes.featureTitle} variant="h6">
                                        Team Ops, Simplified
                                    </Typography>
                                    <Typography className={classes.featureBody}>
                                        Invite teammates, assign questions, and keep your crew aligned even when shifts blur
                                        together. Everyone sees the same mission plan.
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Card className={classes.featureCard}>
                                <CardContent>
                                    <Typography className={classes.featureTitle} variant="h6">
                                        Answer Workflow
                                    </Typography>
                                    <Typography className={classes.featureBody}>
                                        Capture answers, notes, and confidence levels in one consistent flow. The team can move
                                        fast without losing context or history.
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    <Typography className={classes.sectionTitle} variant="h4">
                        Start in three moves
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <Box className={classes.stepsCard}>
                                <Typography className={classes.stepNumber}>Step 1</Typography>
                                <Typography className={classes.stepTitle} variant="h6">
                                    Register your team
                                </Typography>
                                <Typography className={classes.stepBody}>
                                    Create your crew, set your team name, and grab your private access code.
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box className={classes.stepsCard}>
                                <Typography className={classes.stepNumber}>Step 2</Typography>
                                <Typography className={classes.stepTitle} variant="h6">
                                    Invite your players
                                </Typography>
                                <Typography className={classes.stepBody}>
                                    Share the access code so teammates can join instantly and see the game plan.
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box className={classes.stepsCard}>
                                <Typography className={classes.stepNumber}>Step 3</Typography>
                                <Typography className={classes.stepTitle} variant="h6">
                                    Run the marathon
                                </Typography>
                                <Typography className={classes.stepBody}>
                                    Launch the live round, log answers, and keep score as the hours roll by.
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>

                    <Box className={classes.storySection}>
                        <Typography className={classes.sectionTitle} variant="h4" align="center">
                            Your Trivia Weekend, End to End
                        </Typography>
                        <Typography className={classes.subtitle} align="center">
                            Follow the flow from team coordination to visual trivia, live contest tracking, and settings management.
                        </Typography>
                        <Grid container spacing={4} style={{ marginTop: 4 }}>
                            {storyCards.map((card) => (
                                <Grid item xs={12} md={6} key={card.title}>
                                    <Card className={classes.storyCard}>
                                        <CardMedia
                                            component="img"
                                            image={card.image}
                                            title={card.title}
                                            alt={`${card.title} screenshot`}
                                            className={classes.storyMedia}
                                            onClick={() => this.handleStoryOpen(card)}
                                            onKeyDown={(event) => this.handleStoryKeyDown(event, card)}
                                            role="button"
                                            tabIndex={0}
                                            aria-label={`Open ${card.title} image`}
                                            aria-haspopup="dialog"
                                            loading="lazy"
                                            decoding="async"
                                        />
                                        <CardContent className={classes.storyContent}>
                                            <Typography className={classes.storyTitle} variant="h6">
                                                {card.title}
                                            </Typography>
                                            <Typography className={classes.storyBody} variant="body2">
                                                {card.description}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                    <Dialog
                        open={Boolean(activeStory)}
                        onClose={this.handleStoryClose}
                        maxWidth={false}
                        fullWidth
                        PaperProps={{ className: classes.modalPaper }}
                    >
                        <DialogContent className={classes.modalContent}>
                            <IconButton className={classes.modalClose} onClick={this.handleStoryClose} aria-label="close image">
                                <CloseIcon />
                            </IconButton>
                            {activeStory && (
                                <img
                                    src={activeStory.image}
                                    alt={activeStory.title}
                                    className={classes.modalImage}
                                />
                            )}
                        </DialogContent>
                    </Dialog>
                </Box>
            </div>
        );
    }
}


// Instead of taking everything from state, we just want the error messages.
// if you wanted you could write this code like this:
// const mapStateToProps = ({errors}) => ({ errors });
const mapStateToProps = state => ({
    errors: state.errors,
    user: state.user,
});

export default withRouter(connect(mapStateToProps)(withStyles(styles)(Welcome)));
